import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errCode, tokenStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";

const cls = "h-12 rounded-xl border-white/10 bg-white/5";

export default function ChangePasswordPage() {
  const { t, tErr } = useI18n();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) { toast.error(t("pwd.mismatch")); return; }
    if (f.new_password.length < 8 || !/[A-Za-z]/.test(f.new_password) || !/\d/.test(f.new_password)) { toast.error(t("pwd.rule")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      tokenStore.set(data);
      setUser(data.user);
      toast.success(t("pwd.done"));
      navigate("/lobby", { replace: true });
    } catch (err) {
      toast.error(tErr(errCode(err)));
    } finally {
      setBusy(false);
    }
  };

  const field = (k, label, testid, auto) => (
    <label className="block space-y-1.5">
      <span className="text-sm text-slate-300">{label}</span>
      <Input type="password" required value={f[k]} autoComplete={auto} className={cls} data-testid={testid}
        onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </label>
  );

  return (
    <div className="mx-auto max-w-md rise" data-testid="change-password-page">
      <form onSubmit={submit} className="surface space-y-4 p-6">
        <KeyRound className="h-7 w-7 text-amber-400" />
        <h1 className="font-display text-2xl font-extrabold">{t(forced ? "pwd.forcedTitle" : "pwd.title")}</h1>
        {forced && <p className="text-sm text-amber-200" data-testid="change-password-forced-note">{t("pwd.forcedText")}</p>}
        {field("current_password", t(forced ? "pwd.temp" : "pwd.current"), "cp-current-input", "current-password")}
        {field("new_password", t("pwd.new"), "cp-new-input", "new-password")}
        {field("confirm", t("pwd.confirm"), "cp-confirm-input", "new-password")}
        <p className="text-xs text-slate-500">{t("pwd.rule")}</p>
        <button disabled={busy} className="h-12 w-full rounded-full bg-amber-500 font-semibold text-[#0B0D12] disabled:opacity-60" data-testid="cp-submit-btn">
          {t("pwd.submit")}
        </button>
      </form>
    </div>
  );
}
